import React from 'react';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import Table from 'react-bootstrap/Table';
import { useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import { formatDateStr, getProjectStatusLabel, formatProgressPercentage, formatOwnerDisplay } from '../../config_and_helpers/helpers';
import { formatQuoteBudgetDisplay } from '../../utils/quoteFundingDisplay';
import InventoryProgressBar from '../Generic/InventoryProgressBar';
import DeleteButton from '../Generic/DeleteButton';
import { deleteProject } from '../../clients/project_client';
import SortableHeaderCell from '../Generic/SortableHeaderCell';

/**
 * Renders a table of projects with their details and associated actions.
 *
 * @param {Object} props - The props passed to the ProjectTable component.
 * @param {Array} props.projectList - The list of projects to be displayed.
 * @param {Function} props.handleDelete - The function to handle delete actions and refresh the list.
 * @param {Function} props.clearSearchValue - The function to clear the search value.
 *
 * @returns {JSX.Element} The JSX markup for the ProjectTable component.
 */
const ProjectTable = ({
  projectList,
  handleDelete,
  clearSearchValue,
  sortBy = '',
  sortDirection = 'asc',
  onSort = null,
}) => {
  const navigate = useNavigate();
  const isViewer = useIsLabViewer();

  const handleProjectClick = projectId => {
    navigate(`/projects/${projectId}`);
  };

  const formatProjectOwnerForTable = project => {
    if (project?.owner != null && project.owner !== '') {
      return formatOwnerDisplay(project.owner);
    }
    if (project?.owner_name) {
      return String(project.owner_name);
    }
    if (project?.owner_email) {
      return String(project.owner_email);
    }
    if (project?.owner_id != null && project.owner_id !== '') {
      return `User #${project.owner_id}`;
    }
    return 'Unassigned';
  };


  const getStatusBadgeVariant = status => {
    const variantMap = {
      PLANNING: 'secondary',
      QUOTING: 'info',
      ORDERING: 'warning',
      RECEIVING: 'primary',
      COMPLETED: 'success',
      ON_HOLD: 'danger',
    };
    return variantMap[status] || 'secondary';
  };

  return (
    <Table striped bordered hover>
      <thead>
        <tr className="text-center">
          <th>#</th>
          <SortableHeaderCell
            label="Name"
            sortKey="name"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <SortableHeaderCell
            label="Status"
            sortKey="status"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <th>Progress</th>
          <th>Products</th>
          <th>Expected Completion</th>
          <th>Budget</th>
          <th>Owner</th>
          <SortableHeaderCell
            label="Last Updated"
            sortKey="updated_at"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          {!isViewer ? <th>Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {projectList.map((project, index) => (
          <tr key={project.id} className="text-center align-middle">
            <td>{index + 1}</td>
            <td>
              <Button
                variant="link"
                onClick={() => handleProjectClick(project.id)}
                style={{
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  color: '#0d6efd',
                  textDecoration: 'underline',
                }}
              >
                {project.name}
              </Button>
            </td>
            <td>
              <Badge bg={getStatusBadgeVariant(project.status)}>
                {getProjectStatusLabel(project.status)}
              </Badge>
            </td>
            <td>
              <div style={{ minWidth: '100px' }}>
                <InventoryProgressBar
                  currentValue={project.completed_products || 0}
                  totalValue={project.total_products || 0}
                />
                <small className="text-muted d-block mt-1">
                  {formatProgressPercentage(project.progress_percentage)}
                </small>
              </div>
            </td>
            <td>
              {project.completed_products || 0} / {project.total_products || 0}
            </td>
            <td>
              {project.expected_completion_date
                ? formatDateStr(project.expected_completion_date)
                : 'N/A'}
            </td>
            <td>{formatQuoteBudgetDisplay(project) || 'N/A'}</td>
            <td>
              <small className="text-muted">
                {formatProjectOwnerForTable(project)}
              </small>
            </td>
            <td>
              {project.last_updated
                ? formatDateStr(project.last_updated)
                : formatDateStr(project.created_date)}
            </td>
            {!isViewer && (
            <td>
              <DeleteButton
                objectType="project"
                objectName={project.name}
                objectId={project.id}
                deleteFetchFunc={deleteProject}
                onSuccessfulDelete={handleDelete}
              />
            </td>
            )}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default ProjectTable;

